import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { VERTICALS, VERTICAL_ORDER } from '@/data/verticals';
import { api } from '@/lib/api';

function useCountUp(target, active, duration = 1800) {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (!active) return;
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(target * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, active, duration]);
    return value;
}

function StatTile({ stat, active }) {
    const v = useCountUp(stat.value, active);
    const formatted = v >= 1000 ? v.toLocaleString() : v;
    return (
        <div data-testid={`impact-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`} className="border-t border-aurika-borderStrong pt-6">
            <div className="font-display text-5xl md:text-6xl lg:text-7xl font-light leading-none text-aurika-obsidian">
                {formatted}
                <span className="text-aurika-terracotta">{stat.suffix}</span>
            </div>
            <p className="mt-4 text-xs tracking-[0.2em] uppercase text-aurika-ink font-medium">
                {stat.label}
            </p>
        </div>
    );
}

const STORIES = [
    {
        eyebrow: 'Field Notes — Varanasi',
        title: 'The silk that refused to fade.',
        excerpt: 'How six Banarasi weaving families rebuilt a 300-year-old loom cluster with three months of design mentoring and a route to urban markets.',
        image: 'https://images.unsplash.com/photo-1722925407220-b22e1ced9ee9',
    },
    {
        eyebrow: 'Field Notes — Kutch',
        title: 'A monsoon supper that fed a village.',
        excerpt: 'Twenty guests. One long table under the neem. Every plate served that evening funded a year of digital literacy classes in Bhuj.',
        image: 'https://images.pexels.com/photos/14120649/pexels-photo-14120649.jpeg',
    },
    {
        eyebrow: 'Field Notes — Wayanad',
        title: 'When the coffee estate became a classroom.',
        excerpt: 'A three-day corporate offsite turned into a season of mentorship for young rural entrepreneurs across the Nilgiris.',
        image: 'https://images.unsplash.com/photo-1719593622853-c625441bc4e0',
    },
];

const THREE_ES = [
    {
        letter: 'E',
        word: 'Empowerment',
        body: 'Education, skill development, entrepreneurship, mentoring and capacity building — designed with the community, not delivered to it.',
    },
    {
        letter: 'E',
        word: 'Employment',
        body: 'Sustainable livelihoods for artisans, farmers, women, youth and rural entrepreneurs — with dignified pay and market access.',
    },
    {
        letter: 'E',
        word: 'Environmental Sustainability',
        body: 'Circular economy practices, conscious consumption, sustainable sourcing, eco-friendly events and regenerative tourism.',
    },
];

export default function Home() {
    const [stats, setStats] = useState([]);
    const [statsVisible, setStatsVisible] = useState(false);
    const statsRef = useRef(null);

    useEffect(() => {
        api.get('/impact-stats').then((r) => setStats(r.data.stats || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!statsRef.current) return;
        const io = new IntersectionObserver(
            ([e]) => e.isIntersecting && setStatsVisible(true),
            { threshold: 0.2 }
        );
        io.observe(statsRef.current);
        return () => io.disconnect();
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section data-testid="hero-section" className="relative h-[92vh] min-h-[640px] w-full overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/36666146/pexels-photo-36666146.jpeg"
                    alt="Rural fields in India"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-aurika-obsidian/70 via-aurika-obsidian/10 to-transparent" />
                <div className="grain absolute inset-0" />

                <div className="relative z-10 h-full container-editorial flex flex-col justify-between pt-32 pb-14">
                    <div className="flex items-center justify-between text-aurika-bone/85 text-xs tracking-[0.24em] uppercase">
                        <span>Est. 2025</span>
                        <span className="hidden md:inline">A Section 8 Foundation</span>
                    </div>

                    <div className="max-w-5xl">
                        <p className="overline text-aurika-ochre reveal">The Aurika Collective</p>
                        <h1 className="mt-6 font-display font-light text-aurika-bone leading-[0.95] text-balance
                            text-[3.25rem] sm:text-[4.5rem] md:text-[5.5rem] lg:text-[7rem] tracking-[-0.02em]">
                            Creating impact through the way we
                            <em className="italic text-aurika-ochre"> travel, gather, volunteer</em> & celebrate.
                        </h1>
                        <p className="mt-8 max-w-2xl text-aurika-bone/85 text-base md:text-lg font-light leading-relaxed">
                            Aurika brings together communities, businesses and changemakers to create sustainable livelihoods,
                            strengthen rural economies and build a more inclusive future.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/verticals/agritourism" data-testid="hero-cta-primary" className="btn-primary bg-aurika-bone !text-aurika-obsidian border-aurika-bone hover:!bg-aurika-terracotta hover:!text-aurika-bone hover:border-aurika-terracotta">
                                Explore Experiences <ArrowRight size={16} />
                            </Link>
                            <Link to="/partner" data-testid="hero-cta-secondary" className="btn-ghost-light">
                                Partner With Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* MARQUEE / MANIFESTO STRIP */}
            <section aria-hidden="true" className="border-y border-aurika-border bg-aurika-linen overflow-hidden">
                <div className="flex animate-marquee whitespace-nowrap py-5">
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="flex shrink-0 items-center gap-14 pr-14 font-display text-2xl md:text-3xl italic font-light text-aurika-obsidian/80">
                            <span>Travel with intention</span><span className="text-aurika-terracotta">◆</span>
                            <span>Gather with purpose</span><span className="text-aurika-terracotta">◆</span>
                            <span>Volunteer with skill</span><span className="text-aurika-terracotta">◆</span>
                            <span>Celebrate with meaning</span><span className="text-aurika-terracotta">◆</span>
                            <span>Source with dignity</span><span className="text-aurika-terracotta">◆</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* ABOUT / MISSION */}
            <section data-testid="about-preview-section" className="section-y bg-aurika-bone">
                <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-4">
                        <p className="overline">A Note From The Collective</p>
                        <h2 className="mt-6 font-display font-light text-aurika-obsidian leading-[1.02] text-4xl md:text-5xl lg:text-6xl">
                            Impact isn’t
                            <br />
                            an event.
                            <br />
                            <em className="italic text-aurika-terracotta">It’s a way of living.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-aurika-ink text-lg font-light leading-relaxed">
                        <p>
                            We believe impact shouldn’t happen only through donations. It should be woven into how we travel,
                            celebrate, learn, volunteer, source products and build communities.
                        </p>
                        <p>
                            Aurika designs experiences, partnerships and campaigns that connect urban communities, businesses
                            and professionals with rural India — generating livelihoods, preserving culture, strengthening
                            local economies and protecting the environment.
                        </p>
                        <Link to="/about" data-testid="about-preview-cta" className="link-underline text-aurika-terracotta inline-flex items-center gap-2 pt-4 !text-sm tracking-[0.16em] uppercase font-medium">
                            Read our story <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* 3E FRAMEWORK */}
            <section data-testid="three-es-section" className="section-y bg-aurika-linen">
                <div className="container-editorial">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div>
                            <p className="overline">Our Framework</p>
                            <h2 className="mt-4 font-display text-5xl md:text-6xl font-light text-aurika-obsidian leading-[1] tracking-tight">
                                The <span className="italic">3E</span>s of Aurika
                            </h2>
                        </div>
                        <p className="max-w-md text-aurika-ink font-light">
                            Every experience, event and campaign we design lives inside three commitments — the compass we return to on every project.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-aurika-borderStrong border border-aurika-borderStrong">
                        {THREE_ES.map((e, i) => (
                            <div key={e.word} data-testid={`three-es-${i}`} className="bg-aurika-bone p-10 md:p-12 group hover:bg-aurika-sand transition-colors duration-500">
                                <div className="flex items-baseline gap-4">
                                    <span className="font-display text-8xl md:text-9xl font-light leading-none text-aurika-terracotta">{e.letter}</span>
                                    <span className="font-mono-brand text-xs tracking-widest text-aurika-muted">0{i + 1}</span>
                                </div>
                                <h3 className="mt-6 font-display text-3xl md:text-4xl font-normal text-aurika-obsidian">{e.word}</h3>
                                <p className="mt-4 text-aurika-ink font-light">{e.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FOUR VERTICALS */}
            <section data-testid="verticals-section" className="section-y bg-aurika-bone">
                <div className="container-editorial">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16">
                        <div className="md:col-span-8">
                            <p className="overline">Four Verticals — One Purpose</p>
                            <h2 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl font-light text-aurika-obsidian leading-[1] tracking-tight">
                                Where <em className="italic">everyday</em> life meets rural transformation.
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16">
                        {VERTICAL_ORDER.map((slug, idx) => {
                            const v = VERTICALS[slug];
                            return (
                                <Link
                                    key={slug}
                                    to={`/verticals/${slug}`}
                                    data-testid={`vertical-card-${slug}`}
                                    className={`group block ${idx % 2 === 1 ? 'md:mt-16' : ''}`}
                                >
                                    <div className="relative overflow-hidden aspect-[4/5] bg-aurika-sand">
                                        <img
                                            src={v.hero}
                                            alt={v.title}
                                            className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                                        />
                                        <div className="absolute top-4 left-4 bg-aurika-bone px-3 py-1 font-mono-brand text-[10px] tracking-widest">
                                            {v.index}
                                        </div>
                                    </div>
                                    <div className="mt-6 flex items-start justify-between gap-6">
                                        <div>
                                            <p className="overline">{v.eyebrow}</p>
                                            <h3 className="mt-3 font-display text-3xl md:text-4xl font-light text-aurika-obsidian group-hover:text-aurika-terracotta transition-colors duration-500">
                                                {v.title}
                                            </h3>
                                            <p className="mt-3 text-aurika-ink font-light max-w-lg">{v.tagline}</p>
                                        </div>
                                        <ArrowUpRight className="mt-2 shrink-0 text-aurika-obsidian group-hover:text-aurika-terracotta group-hover:rotate-45 transition-all duration-500" size={26} />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* IMPACT NUMBERS */}
            <section data-testid="impact-section" className="section-y bg-aurika-obsidian text-aurika-bone relative">
                <div className="container-editorial" ref={statsRef}>
                    <div className="max-w-3xl">
                        <p className="overline text-aurika-ochre">Looking Ahead</p>
                        <h2 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl font-light leading-[1] text-balance">
                            The impact we’re <em className="italic text-aurika-ochre">striving</em> to create.
                        </h2>
                        <p className="mt-6 text-aurika-bone/75 max-w-xl font-light">
                            Every number below represents the impact we aspire to create alongside our partners, communities, and supporters.
                        </p>
                    </div>

                    <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-14">
                        {stats.map((s) => (
                            <div key={s.label} className="border-t border-aurika-bone/25 pt-6">
                                <div className="font-display text-5xl md:text-6xl lg:text-7xl font-light leading-none text-aurika-bone">
                                    {s.prefix && <span>{s.prefix}</span>}
                                    <StatValue target={s.value} active={statsVisible} />
                                    <span className="text-aurika-ochre">{s.suffix}</span>
                                </div>
                                <p className="mt-4 text-[11px] tracking-[0.2em] uppercase text-aurika-bone/70">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STORIES */}
            <section data-testid="stories-section" className="section-y bg-aurika-bone">
                <div className="container-editorial">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
                        <div className="lg:max-w-xl">
                            <p className="overline">Illustrative Impact Stories</p>
                            <h2 className="mt-4 font-display text-5xl md:text-6xl font-light text-aurika-obsidian leading-[1]">
                                The futures we’re <em className="italic">working</em> to make possible.
                            </h2>
                        </div>
                        <p className="lg:max-w-md text-aurika-ink font-light">
                            These fictional case studies illustrate how Aurika collaborates with communities, businesses and institutions to create meaningful, measurable impact.
                        </p>
                    </div>

                    <div className="space-y-24">
                        {STORIES.map((s, i) => (
                            <article key={s.title} data-testid={`story-${i}`} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                                <div className="lg:col-span-7 overflow-hidden">
                                    <img src={s.image} alt={s.title} className="w-full aspect-[16/10] object-cover" />
                                </div>
                                <div className="lg:col-span-4 lg:col-start-9">
                                    <p className="overline">{s.eyebrow}</p>
                                    <h3 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-light text-aurika-obsidian leading-[1.05]">
                                        {s.title}
                                    </h3>
                                    <p className="mt-5 text-aurika-ink font-light">{s.excerpt}</p>
                                    <button data-testid={`story-cta-${i}`} className="link-underline mt-6 !text-sm tracking-[0.16em] uppercase text-aurika-terracotta font-medium inline-flex items-center gap-2">
                                        Read the field note <ArrowUpRight size={14} />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* PARTNER CTA */}
            <section data-testid="partner-cta-section" className="relative overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/32277759/pexels-photo-32277759.jpeg"
                    alt="Farmer portrait"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-aurika-obsidian/60" />
                <div className="grain absolute inset-0" />
                <div className="relative container-editorial py-32 md:py-48 text-aurika-bone">
                    <div className="max-w-3xl">
                        <p className="overline text-aurika-ochre">Create Impact Together</p>
                        <h2 className="mt-6 font-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1] text-balance">
                            Whether you travel, volunteer, celebrate or collaborate — every experience builds a stronger rural India.
                        </h2>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/partner" data-testid="partner-cta-primary" className="btn-primary bg-aurika-bone !text-aurika-obsidian border-aurika-bone hover:!bg-aurika-terracotta hover:!text-aurika-bone hover:border-aurika-terracotta">
                                Start a conversation <ArrowRight size={16} />
                            </Link>
                            <Link to="/about" data-testid="partner-cta-secondary" className="btn-ghost-light">
                                Understand our approach
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function StatValue({ target, active }) {
    const v = useCountUp(target, active);
    return <>{v >= 1000 ? v.toLocaleString() : v}</>;
}
